import { useEffect, useState } from "react";
import "./App.css";
import { HashRouter, useLocation } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import SideBar from "./SideBar";
import Overview from "./Overview";
import Invoices from "./Invoices";
import Account from "./Account";
import { Navigate } from "react-router-dom";
import { ConsumptionBar, InvoiceStatus, RequiredData } from "./Types";
import Preload from "./Preload";
import { fakeData } from "./fake";
import { formatName, roundTo, sleep } from "./Utils";
import { callMinaSidorApi, getAccessToken } from "./Utils/api";
import BankIDLoginModal from "./BankIDLoginModal";
import { PaymentOptions } from "./Account/SettingsView";
import { isMobile } from "react-device-detect";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import config from "./config";

//  Sätt till true så används fejk-data från fejk.ts
const useFakeData = false;

//  Följande typer är vad vi egentligen får från Mina Sidor-API. Men, vi har mycket snyggare typer, så vi gör om deras JSON till exempelvis Customer med en map().
type MinaSidorApiCustomer = {
  id: string;
  first_name: string;
  last_name: string;
  zip_code: string;
  address1: string;
  city: string;
  email: string;
};

type MinaSidorApiCustomerSite = {
  id: string;
  address1: string;
  zip_code: string;
  city: string;
  start_date: string;
  active_payment_type_id: string;
};

type MinaSidorApiCustomerSitePriceGroup = {
  id: string;
  misc: {
    active: string;
    ep_group_description: string;
    binding_period: string;
    notice_to_quit: string;
  };
};

type MinaSidorApiInvoice = {
  id: string;
  invoice_date: string;
  invoice_status: string;
  invoice_pdf: string;
  invoice_lines: { amount: string; }[];
};

export type ConsumptionSites = { [siteId: number]: ConsumptionBar[] };

type MinaSidorApiMonthlyDataPoint = { amount_of_days: string; volume: string; month: string; };
type MinaSidorApiDailyDataPoint = { amount_of_hours: string; volume: string; date: string; status: string; };

async function fetchGraphData(siteIds: number[]): Promise<{ daily: ConsumptionSites; monthly: ConsumptionSites }> {
  const fromDate = "2020-01-01";
  const toDate = new Date().toISOString().slice(0, 10);

  const results = await Promise.all(
    siteIds.map((siteId) =>
      callMinaSidorApi<{ dailyReadings: MinaSidorApiDailyDataPoint[]; monthlyReadings: MinaSidorApiMonthlyDataPoint[]; }>(
        "post",
        `/zavann/get_energy_data.php?siteIds[]=${siteId}&from=${fromDate}&to=${toDate}`
      ).then((result) => ({ siteId, result }))
    )
  );

  const daily: ConsumptionSites = {};
  const monthly: ConsumptionSites = {};
  for (const { siteId, result } of results) {
    daily[siteId] = result.dailyReadings.map((day) => ({
      amountOfAvg: Number(day.amount_of_hours),
      totalVolume: Number(day.volume),
      date: new Date(day.date),
      status: day.status,
    } as ConsumptionBar));
    monthly[siteId] = result.monthlyReadings.map((month) => ({
      amountOfAvg: Number(month.amount_of_days),
      totalVolume: Number(month.volume),
      date: new Date(month.month),
    } as ConsumptionBar));
  }

  return { daily, monthly };
}

function isTokenExpiredError(error: any) {
  return error?.error?.includes("user token is invalid");
}

async function fetchData(): Promise<RequiredData> {
  if (useFakeData) {
    return fakeData;
  }

  while (true) {
    try {
      const { customer, sites } = await callMinaSidorApi<{
        customer: MinaSidorApiCustomer;
        sites: (MinaSidorApiCustomerSite & { invoices: MinaSidorApiInvoice[]; priceGroups: MinaSidorApiCustomerSitePriceGroup[] })[];
      }>('post', `/zavann/get_energy_data.php`);


      if (!sites.length) {
        alert("Inga registrerade anläggningar, problem kan förekomma på sidan.");
      }

      const allInvoices = sites.flatMap(site =>
        site.invoices.map(invoice => ({ ...invoice, siteId: site.id }))
      );

      // Visa senaste fakturan först. localCompare jämför två strängar och returnerar -1, 0 eller 1.
      allInvoices.sort((a, b) => b.invoice_date.localeCompare(a.invoice_date));
      return {
        customer: {
          firstName: formatName(customer.first_name),
          lastName: formatName(customer.last_name),
          id: Number(customer.id),
          adress: customer.address1,
          city: formatName(customer.city),
          email: customer.email,
          zipCode: Number(customer.zip_code.replace(/[\s]/g, '')), // Vi måste ta bort eventuella mellanslag i postnumret för att kunna gör om det till ett nummer.
        },
        invoices: allInvoices.map(invoice => ({
          id: Number(invoice.id),
          link: invoice.invoice_pdf,
          price: roundTo(invoice.invoice_lines?.reduce((sum, invoiceLine) => sum + Number(invoiceLine.amount), 0) || 0, 2),
          status: invoice.invoice_status as InvoiceStatus,
          siteId: Number(invoice.siteId)
        })),
        subscriptions: sites.map(site => {
          const activePriceGroup = site.priceGroups.find(priceGroup => Number(priceGroup.misc.active)); // active verkar vara "1" på någon.

          return {
            adress: site.address1,
            id: Number(site.id),
            agreement: activePriceGroup.misc.ep_group_description,
            period: Number(/(\d+) mån/iu.exec(activePriceGroup?.misc.notice_to_quit)?.[1]) || null, // binding_period kan vara "Saknas" och period kommer isåfall bli null.
            startDate: new Date(site.start_date),
            paymentName: { "1": PaymentOptions.PDF, "2": PaymentOptions.Autogiro, "3": PaymentOptions.Pappersfaktura }[site.active_payment_type_id]
          }
        })
      }
    } catch (error) {
      console.error("Could not fetch required data", error);
      if (isTokenExpiredError(error)) {
        throw error; // Denna typ av error ska tvinga användaren att logga in på nytt.
      }

      await sleep(2500); // Vänta ett tag innan vi försöker igen.
    }
  }
}

function App() {
  const [data, setData] = useState<RequiredData | null>(null);
  const [isLoggedIn, setLoggedIn] = useState(Boolean(getAccessToken()));
  const [graphDaily, setGraphDaily] = useState<ConsumptionSites>({});
  const [graphMonthly, setGraphMonthly] = useState<ConsumptionSites>({});
  const [isFetchingGraph, setFetchingGraph] = useState(false);
  const [defaultDate, setDefaultDate] = useState<string>(null);
  useEffect(() => {
    const globalStyle = document.documentElement.style;
    globalStyle.setProperty('--color-primary', config.colors.primary);
    globalStyle.setProperty('--color-primary-hover', config.colors.primaryHover);
    globalStyle.setProperty('--color-primary-light', config.colors.primaryLight);
    globalStyle.setProperty('--color-primary-lighter', config.colors.primaryLighter);
    globalStyle.setProperty('--color-primary-faint', config.colors.primaryFaint);
    globalStyle.setProperty('--color-primary-muted', config.colors.primaryMuted);

    const favicon = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (favicon) {
      favicon.href = config.faviconUrl;
    }

    const appleIcon = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
    if (appleIcon) {
      appleIcon.href = config.faviconUrl;
    }

    const themeColor = document.querySelector<HTMLMetaElement>("meta[name='theme-color']");
    if (themeColor) {
      themeColor.content = config.colors.primary;
    }

    const ogImage = document.querySelector<HTMLMetaElement>("meta[property='og:image']");
    if (ogImage) {
      ogImage.content = window.location.origin + config.decorativeImageUrl;
    }

    const ogImageAlt = document.querySelector<HTMLMetaElement>("meta[property='og:image:alt']");
    if (ogImageAlt) {
      ogImageAlt.content = config.companyName;
    }

    const ogTitle = document.querySelector<HTMLMetaElement>("meta[property='og:title']");
    if (ogTitle) {
      ogTitle.content = config.companyName;
    }

    const appName = document.querySelector<HTMLMetaElement>("meta[name='application-name']");
    if (appName) {
      appName.content = config.companyName;
    }

    document.title = config.companyName + " – Mina sidor";
  }, []);

  function getEarliestMonthWithData(graphData: ConsumptionBar[]) {
    for (let i = graphData.length; i > 0; i--) {
      if (graphData[i]?.totalVolume) {
        return graphData[i].date.toISOString().slice(0, 7);
      }
    }
  }

  useEffect(() => {
    if (isLoggedIn) {
      fetchData().then(setData).catch(error => {
        if (isTokenExpiredError(error)) {
          setLoggedIn(false);
        }
      });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!data) return;
    setFetchingGraph(true);
    fetchGraphData(data.subscriptions.map(s => s.id))
      .then(({ daily, monthly }) => {
        setGraphDaily(daily);
        setGraphMonthly(monthly);
        setFetchingGraph(false);

        const firstSiteId = data.subscriptions[0].id;
        const earliestMonth = getEarliestMonthWithData(monthly[firstSiteId]);
        setDefaultDate(earliestMonth);
      })
      .catch(error => console.error("Could not fetch graph data", error));
  }, [data]);

  return (
    <div className="App">
      {
        data && defaultDate ? (
          <>
            <HashRouter>
              <SideBar />
              <div className={classNames({
                "route-container": true,
                "is-mobile": isMobile
              }
              )}>
                <Header />
                <Routes>
                  <Route path="dashboard/:siteId/:scope/:filter" element={<Overview customer={data.customer} subscriptions={data.subscriptions} daily={graphDaily} monthly={graphMonthly} isFetchingData={isFetchingGraph} />}></Route>
                  <Route path="dashboard/:siteId?/:scope?" element={<Navigate to={"/dashboard/" + data.subscriptions[0].id + "/daily/" + defaultDate} />}></Route>
                  <Route path="invoices" element={<Invoices invoices={data.invoices} subscriptions={data.subscriptions} />}></Route>
                  <Route path="invoices/:siteId" element={<Invoices invoices={data.invoices} subscriptions={data.subscriptions} siteId={data.subscriptions[0].id} />}></Route>
                  <Route path="account" element={<Account subscriptions={data.subscriptions} customer={data.customer} />}></Route>
                  <Route path="" element={<Navigate to="dashboard" replace />}></Route>
                </Routes>
              </div>
            </HashRouter>
            <GraphicElement />
            <BackToHome />
          </>
        ) : null
      }

      {
        //  Innan vi har data, visa en preloader, och rendera alltid då vi fade:ar ut preloader när vi har data.
        <Preload visible={!data || !defaultDate} />
      }

      <BankIDLoginModal
        onAuthenticated={result => {
          localStorage.setItem('minasidor_token', result.userToken);
          setLoggedIn(true);
        }}
        onBankIdSessionCreated={sessionId => {
          localStorage.setItem("minasidor_session_id", sessionId);
        }}
        visible={!isLoggedIn} />
    </div>
  );
}

export default App;

function BackToHome() {

  function logOut() {
    localStorage.removeItem('vuex');
    localStorage.removeItem('minasidor_token');
    localStorage.removeItem('minasidor_session_id');
  }

  return (
    <div className="back-home-container">
      <a onClick={logOut} href={config.logoutUrl}><FontAwesomeIcon icon={faArrowRightFromBracket} /> Logga ut</a>
    </div>
  )
}


function Header() {
  const location = useLocation();

  function getViewTitle(location: string) {
    switch (location) {
      case "/dashboard":
        return "Översikt"
      case "/invoices":
        return "Betalningar"
      case "/account":
        return "Mitt konto"
      default: return "Översikt"
    }
  }
  return (<div className="header">
    <h1 className="view-title">{getViewTitle(location.pathname)}</h1>
  </div>)
}

function GraphicElement() {
  return (<img className="graphic-element" src={config.decorativeImageUrl} alt={config.companyName + " logotyp"} />)
}